// ABOUTME: SPEC-82 §4.2 — assembles the props App.js passes down for the voice-first experiment: the footers' `voiceFirst`
// ABOUTME: object (null when no bottom toggle applies), the top-chrome layout, the Views switch and the collection menu.

import { useEffect, useMemo } from 'react';
import useVoiceDockFooter from './useVoiceDockFooter';
import useCollectionMenu from '../components/VoiceFirst/CollectionMenu/useCollectionMenu';
import { resolveTopChrome, desktopIndicatorTop, railExpandedFor } from './topChromeLayout';
import { RAIL_UNIT } from '../components/ReelChrome/railGeometry';

/**
 * SPEC-82 Rev 3 (bug 4): the menu, not the right-hand rail, hosts the consent panel whenever it owns Privacy (flag-off: the rail is
 * told exactly `overlay` and the menu hosts nothing).
 */
function useMenuPrivacy(topChrome, { overlay = null, consent, onSave, onOpenPrivacyPolicy }, onTogglePanel) {
  const menuPrivacy = useMemo(() => (topChrome.railHidesPrivacy
    ? { open: overlay === 'PRIVACY', consent, onSave, onOpenPrivacyPolicy, onCollapse: () => onTogglePanel?.('PRIVACY') }
    : null), [topChrome.railHidesPrivacy, overlay, consent, onSave, onOpenPrivacyPolicy, onTogglePanel]);
  return { railExpanded: railExpandedFor(overlay, topChrome), menuPrivacy };
}

/**
 * @param {object} p
 * @param {Readonly<Record<string, true>>} p.ux the readUiExperiment() map
 * @param {object} p.introX useIntroExperiment()'s return (the tap gate: holding / ctaVisible / tap)
 * @param {string} p.brandName from /api/config — never a literal here
 * @param {'desktop'|'mobile'} p.composition the app's own composition
 * @param {boolean} [p.footerShown] the footers are mounted (Enter pressed, film over); gates publishing the open row's lift
 * @param {object} p.menuInputs useCollectionMenu's inputs minus `enabled` (stage, collections, titles, buttons, fade, film, privacy)
 * @param {object} [p.privacyInputs] Rev 3 (bug 4): `{ overlay, consent, onSave, onOpenPrivacyPolicy }` — App's panel slot and the
 *   consent handlers the rail already gets, so the menu can host the consent panel under the hamburger
 * @returns {{ footer: (object|null), topChrome: object, hideViews: boolean, menu: object, desktopIndicatorStyle: (object|null),
 *             railExpanded: (string|null), menuPrivacy: (object|null) }}
 */
export default function useVoiceFirstChrome({
  ux, introX, brandName, composition, footerShown = true, menuInputs, privacyInputs = {}, connecting = false,
}) {
  // Names only, once per config read, and nothing at all on a tenant without the experiment (no console noise flag-off).
  useEffect(() => {
    const names = Object.keys(ux);
    if (names.length) console.log(`[SPEC-82] ux=${names.join(',')}`);
  }, [ux]);
  const { footer } = useVoiceDockFooter({ ux, introX, brandName, footerShown, connecting });

  const topChrome = useMemo(() => resolveTopChrome({ composition, ux }), [composition, ux]);
  const menu = useCollectionMenu({ enabled: !!ux.collection_menu, ...menuInputs });

  // Desktop only: where the connection indicator goes once the bags take its corner (null = today's corner).
  const desktopIndicatorStyle = useMemo(
    () => (composition === 'desktop' && topChrome.indicator === 'under-wordmark'
      ? { '--u': RAIL_UNIT, top: desktopIndicatorTop(), left: '50%', transform: 'translateX(-50%)' }
      : null),
    [composition, topChrome.indicator],
  );

  const { railExpanded, menuPrivacy } = useMenuPrivacy(topChrome, privacyInputs, menuInputs?.onTogglePanel);

  return { footer, topChrome, hideViews: !!ux.hide_views, menu, desktopIndicatorStyle, railExpanded, menuPrivacy };
}
